import { useEffect, useState, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/form';
import {
  Select as ShadcnSelect,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/shared/ui/select';
import { DatePicker } from '@/shared/ui/date-picker';
import { useToast } from '@/shared/hooks/use-toast';
import {
  useCreateCollection,
  useUpdateCollection,
  useCollection,
} from '../model';
import { CollectionRedirectType, type CreateCollectionPayload, type UpdateCollectionPayload, type CollectionTranslation, type CollectionItem } from '../types';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, GripVertical } from 'lucide-react';
import { collectionFormSchema, type CollectionFormValues, type TranslationFormValue, type ItemFormValue } from '../model';
import { FormFooter } from '@/shared/widgets';
import { Input } from '@/shared/ui/input';
import { Switch } from '@/shared/ui/switch';
import { FileUpload } from '../components/FileUpload';
import { Badge } from '@/shared/ui/badge';
import { SelectOption } from '@/shared/widgets/react-select';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/ui/avatar';
import { components, OptionProps } from 'react-select';
import ReactSelectBase from 'react-select';
import { StylesConfig } from 'react-select';
import { useQuery } from '@tanstack/react-query';
import api from '@/shared/api/api';
import type { PaginatedResponse } from '@/features/profiles/services/profiles/types';
import type { IPreset } from '@/features/packs/services/presets/presetService';
import { useTranslation } from '@/shared/i18n';

const formatDateForApi = (date?: Date): string | undefined => {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;
};

const parseDateFromBackend = (dateString?: string): Date | undefined => {
  if (!dateString) return undefined;
  try {
    // A API pode retornar datas no formato "YYYY-MM-DD" (sem hora)
    // Precisamos garantir que seja parseado corretamente
    const date = new Date(dateString);
    // Se a data for inválida, tenta adicionar hora para garantir parse correto
    if (isNaN(date.getTime()) && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return new Date(`${dateString}T00:00:00.000Z`);
    }
    return isNaN(date.getTime()) ? undefined : date;
  } catch {
    return undefined;
  }
};

const LANGUAGE_OPTIONS: SelectOption[] = [
  { value: 'PT_BR', label: 'Português (Brasil)' },
  { value: 'EN', label: 'English' },
  { value: 'ES', label: 'Español' },
];

type FormData = CollectionFormValues;

// Componente SortableItem para drag and drop
interface SortableItemProps {
  field: { id: string; itemId?: string };
  index: number;
  itemName: string;
  itemImage?: string;
  onRemove: (index: number) => void;
}

const SortableItem = ({ field, index, itemName, itemImage, onRemove }: SortableItemProps) => {
  const { t } = useTranslation('collections');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="bg-white/[0.02] border-white/[0.04]">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing"
            >
              <GripVertical className="h-5 w-5 text-white/40" />
            </div>
            {itemImage && (
              <Avatar className="h-10 w-10 rounded-md">
                <AvatarImage src={itemImage} alt={itemName} />
                <AvatarFallback className="rounded-md bg-white/10 text-xs">
                  {itemName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            )}
            <Badge variant="outline">Preset</Badge>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">
                {itemName}
              </div>
              {field.itemId && (
                <div className="text-xs text-muted-foreground">
                  ID: {field.itemId}
                </div>
              )}
            </div>
            <span className="text-sm text-muted-foreground">{t('order')}: {index + 1}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onRemove(index)}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export function CollectionForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { toast } = useToast();

  const { collection, isLoading: loadingCollection } = useCollection(id, { enabled: isEdit });
  const { createCollection, isLoading: creating } = useCreateCollection();
  const { updateCollection, isLoading: updating } = useUpdateCollection();
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [itemSearchQuery, setItemSearchQuery] = useState('');

  const form = useForm<FormData>({
    resolver: zodResolver(collectionFormSchema),
    defaultValues: {
      name: '',
      translations: [],
      items: [],
      banner: {
        redirectType: CollectionRedirectType.LIST_OF_PRESETS,
        isActive: false,
        startDate: undefined,
        endDate: undefined,
      },
    },
  });

  const { fields: translationFields, append: appendTranslation, remove: removeTranslation } = useFieldArray({
    control: form.control,
    name: 'translations',
  });

  const { fields: itemFields, append: appendItem, remove: removeItem, move: moveItem } = useFieldArray({
    control: form.control,
    name: 'items',
  });

  // Sensors para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Observar o tipo do banner para determinar se mostra a seleção de itens
  const bannerRedirectType = form.watch('banner.redirectType');
  const shouldShowItems = bannerRedirectType === CollectionRedirectType.PRESETS || bannerRedirectType === CollectionRedirectType.LIST_OF_PRESETS;

  // Função para lidar com o fim do drag
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = itemFields.findIndex((field) => field.id === active.id);
      const newIndex = itemFields.findIndex((field) => field.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        moveItem(oldIndex, newIndex);
      }
    }
  };

  // Buscar presets usando a mesma rota da tela inicial
  const { data: presetsData, isLoading: isLoadingPresets } = useQuery({
    queryKey: ['presets-search', itemSearchQuery],
    queryFn: async () => {
      if (itemSearchQuery.length < 2) return { data: [], page: 1, limit: 20, totalPage: 0, totalItems: 0 } as PaginatedResponse<IPreset>;
      // Usar a mesma estrutura de parâmetros da tela inicial
      const response = await api<PaginatedResponse<IPreset>>({
        url: '/v1/presets',
        method: 'GET',
        params: { 
          name: itemSearchQuery, 
          limit: 20,
          // Parâmetros opcionais que podem ser usados na tela inicial
          // published: true, // descomente se necessário
        },
      });
      return response;
    },
    enabled: itemSearchQuery.length >= 2 && shouldShowItems,
  });

  // Tipo estendido para incluir dados completos do preset
  interface PresetSelectOption extends SelectOption {
    preset?: IPreset;
  }

  // Estilos customizados para o select de presets
  const presetSelectStyles: StylesConfig<PresetSelectOption, false> = {
    control: (base, state) => ({
      ...base,
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      borderColor: state.isFocused ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
      borderWidth: '1px',
      minHeight: '40px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(255, 255, 255, 0.1)' : 'none',
      '&:hover': {
        borderColor: 'rgba(255, 255, 255, 0.2)',
      },
    }),
    valueContainer: (base) => ({
      ...base,
      color: 'white',
    }),
    input: (base) => ({
      ...base,
      color: 'white',
    }),
    placeholder: (base) => ({
      ...base,
      color: 'rgba(255, 255, 255, 0.5)',
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: '#2A2A2A',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '6px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
      color: 'white',
      padding: '12px',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    }),
  };

  // Componente customizado para mostrar mais informações do preset
  const PresetOption = (props: OptionProps<PresetSelectOption>) => {
    const { preset } = props.data;
    const creatorName = preset?.pack?.packsCreators?.[0]?.creators?.name || 'N/A';
    const packName = preset?.pack?.name || 'N/A';
    const isFree = preset?.isFree || false;
    
    return (
      <components.Option {...props}>
        <div className="flex items-center gap-3 py-1">
          {/* Imagem do preset */}
          {preset?.cover && (
            <div className="flex-shrink-0">
              <Avatar className="h-12 w-12 rounded-md">
                <AvatarImage src={preset.cover} alt={preset.name} />
                <AvatarFallback className="rounded-md bg-white/10 text-xs">
                  {preset.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>
          )}
          
          {/* Informações do preset */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-white truncate">{preset?.name}</span>
              {isFree && (
                <Badge variant="outline" className="text-xs bg-green-600/20 text-green-400 border-green-600/30">
                  Grátis
                </Badge>
              )}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              <span className="truncate">{packName}</span>
              {creatorName !== 'N/A' && (
                <span className="text-gray-500"> • {creatorName}</span>
              )}
            </div>
            {preset?.translations?.[0]?.about && (
              <div className="text-xs text-gray-500 mt-0.5 truncate">
                {preset.translations[0].about}
              </div>
            )}
          </div>
        </div>
      </components.Option>
    );
  };

  // Componente customizado para o valor selecionado
  const PresetSingleValue = (props: any) => {
    const { data } = props;
    const { preset } = data as PresetSelectOption;
    return (
      <components.SingleValue {...props}>
        <div className="flex items-center gap-2">
          {preset?.cover && (
            <Avatar className="h-6 w-6 rounded-md">
              <AvatarImage src={preset.cover} alt={preset.name} />
              <AvatarFallback className="rounded-md bg-white/10 text-xs">
                {preset.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          )}
          <span className="text-white">{preset?.name || data.label}</span>
        </div>
      </components.SingleValue>
    );
  };

  const { t } = useTranslation('collections');
  const itemOptions: PresetSelectOption[] = useMemo(() => {
    if (!shouldShowItems) return [];
    return (presetsData?.data || []).map(preset => ({
      value: preset.id,
      label: preset.name,
      preset,
    }));
  }, [presetsData, shouldShowItems]);

  useEffect(() => {
    if (collection) {
      form.reset({
        name: collection.name,
        translations: collection.translations || [],
        items: collection.items || [],
        banner: collection.banner
          ? {
              redirectType: collection.banner.redirectType || CollectionRedirectType.LIST_OF_PRESETS,
              isActive: collection.banner.isActive,
              startDate: parseDateFromBackend(collection.banner.startDate),
              endDate: parseDateFromBackend(collection.banner.endDate),
            }
          : {
              redirectType: CollectionRedirectType.LIST_OF_PRESETS,
              isActive: false,
              startDate: undefined,
              endDate: undefined,
            },
      });
      if (collection.cover) {
        setCoverPreview(collection.cover);
      }
      
      // Carregar preview do banner se existir
      if (collection.bannerImageUrl) {
        setBannerPreview(collection.bannerImageUrl);
      }
    }
  }, [collection, form]);

  // Preview de imagens
  useEffect(() => {
    if (coverFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result as string);
      };
      reader.readAsDataURL(coverFile);
    }
  }, [coverFile]);

  useEffect(() => {
    if (bannerFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPreview(reader.result as string);
      };
      reader.readAsDataURL(bannerFile);
    }
  }, [bannerFile]);

  const isSubmitting = creating || updating;

  const onSubmit = async (data: FormData) => {
    try {
      if (isEdit && id) {
        // Update: incluir ids
        const payload: UpdateCollectionPayload = {
          collection: {
            id: collection?.id, // ID da collection
            name: data.name,
            translations: data.translations && data.translations.length > 0 
              ? (data.translations as CollectionTranslation[])
              : [],
            items: data.items && data.items.length > 0 
              ? data.items
                  .filter((item): item is { itemId: string } => 
                    Boolean(item.itemId)
                  )
                  .map((item, index) => ({
                    itemId: item.itemId,
                    displayOrder: index + 1,
                  }))
              : [],
          },
        };

        if (data.banner && data.banner.startDate && data.banner.endDate) {
          payload.banner = {
            id: collection?.banner?.id, // ID do banner obtido da resposta da API
            redirectType: data.banner.redirectType,
            isActive: data.banner.isActive,
            startDate: formatDateForApi(data.banner.startDate)!,
            endDate: formatDateForApi(data.banner.endDate)!,
          };
        }

        if (coverFile) {
          payload.coverImage = coverFile;
        }

        if (bannerFile) {
          payload.bannerImage = bannerFile;
        }

        await updateCollection(id, payload);
        navigate('/mobile/collections');
        return;
      }

      // Create: sem ids
      const payload: CreateCollectionPayload = {
        collection: {
          name: data.name,
          translations: data.translations && data.translations.length > 0 
            ? (data.translations as CollectionTranslation[])
            : [],
          items: data.items && data.items.length > 0 
            ? data.items
                .filter((item): item is { itemId: string } => 
                  Boolean(item.itemId)
                )
                .map((item, index) => ({
                  itemId: item.itemId,
                  displayOrder: index + 1,
                }))
            : [],
        },
      };

      if (data.banner && data.banner.startDate && data.banner.endDate) {
        payload.banner = {
          redirectType: data.banner.redirectType,
          isActive: data.banner.isActive,
          startDate: formatDateForApi(data.banner.startDate)!,
          endDate: formatDateForApi(data.banner.endDate)!,
        };
      }

      if (coverFile) {
        payload.coverImage = coverFile;
      }

      if (bannerFile) {
        payload.bannerImage = bannerFile;
      }

      await createCollection(payload);
      navigate('/mobile/collections');
    } catch (error) {
      console.error('Erro ao salvar collection:', error);
    }
  };

  const handleAddItem = (itemId: string) => {
    const newItem: ItemFormValue = {
      itemId,
      displayOrder: itemFields.length + 1,
    };
    appendItem(newItem);
    setItemSearchQuery('');
  };

  if (isEdit && loadingCollection) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/mobile/collections')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('loading')}</h1>
            <p className="text-muted-foreground">{t('loadingCollection')}</p>
          </div>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/mobile/collections')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEdit ? t('editCollection') : t('newCollection')}
          </h1>
          <p className="text-muted-foreground">
            {isEdit ? t('updateCollectionInfo') : t('fillCollectionData')}
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Seção Collection */}
          <Card className="bg-white/[0.03] border-white/[0.06]">
            <CardHeader>
              <CardTitle>{t('collection')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('name')} *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t('collectionNamePlaceholder')} />
                    </FormControl>
                    <FormDescription>{t('collectionNameDescription')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Cover Image */}
              <FileUpload
                label={t('coverImage')}
                description={t('coverImageDescription')}
                preview={coverPreview}
                onFileChange={(file) => {
                  setCoverFile(file);
                  if (!file) {
                    setCoverPreview(null);
                  }
                }}
              />

              {/* Translations */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-semibold">{t('translations')}</h3>
                    <p className="text-sm text-muted-foreground">{t('addTranslationDescription')}</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => appendTranslation({ languageCode: 'PT_BR', description: '' })}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('addTranslation')}
                  </Button>
                </div>

                {translationFields.map((field, index) => (
                  <Card key={field.id} className="bg-white/[0.02] border-white/[0.04]">
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        <FormField
                          control={form.control}
                          name={`translations.${index}.languageCode`}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormLabel>{t('language')}</FormLabel>
                              <ShadcnSelect value={field.value} onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder={t('selectLanguage')} />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {LANGUAGE_OPTIONS.map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </ShadcnSelect>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`translations.${index}.description`}
                          render={({ field }) => (
                            <FormItem className="flex-[2]">
                              <FormLabel>{t('description')}</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder={t('translatedDescription')} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <div className="flex items-end pb-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeTranslation(index)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Items - Mostrar apenas quando o tipo do banner for PRESETS */}
              {shouldShowItems && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-semibold">{t('items')}</h3>
                      <p className="text-sm text-muted-foreground">{t('addItemsDescription')}</p>
                    </div>
                  </div>

                  {/* Adicionar Item */}
                  <Card className="bg-white/[0.02] border-white/[0.04]">
                    <CardContent className="p-4 space-y-4">
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <ReactSelectBase
                            options={itemOptions}
                            value={null}
                            onChange={(option: PresetSelectOption | null) => {
                              if (option && option.value) {
                                handleAddItem(option.value);
                              }
                            }}
                            onInputChange={(inputValue) => {
                              if (inputValue.length >= 2) {
                                setItemSearchQuery(inputValue);
                              } else {
                                setItemSearchQuery('');
                              }
                            }}
                            isLoading={isLoadingPresets}
                            placeholder={t('searchPresets')}
                            noOptionsMessage={() => 
                              itemSearchQuery.length < 2 
                                ? t('typeAtLeast2Characters') 
                                : t('noResultsFound')
                            }
                            isClearable
                            isSearchable
                            styles={presetSelectStyles}
                            components={{
                              Option: PresetOption,
                              SingleValue: PresetSingleValue,
                            }}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            formatOptionLabel={(option) => (
                              <div className="flex items-center gap-2">
                                {option.preset?.cover && (
                                  <Avatar className="h-8 w-8 rounded-md">
                                    <AvatarImage src={option.preset.cover} alt={option.preset.name} />
                                    <AvatarFallback className="rounded-md bg-white/10 text-xs">
                                      {option.preset.name.charAt(0).toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                )}
                                <span className="text-white">{option.label}</span>
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Lista de Items */}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={itemFields.map((field) => field.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-3">
                        {itemFields.map((field, index) => {
                          const item = form.watch(`items.${index}`);
                          const itemName = item?.name || 'Preset';
                          const itemImage = item?.image;
                          
                          return (
                            <SortableItem
                              key={field.id}
                              field={field}
                              index={index}
                              itemName={itemName}
                              itemImage={itemImage}
                              onRemove={removeItem}
                            />
                          );
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Seção Banner */}
          <Card className="bg-white/[0.03] border-white/[0.06]">
            <CardHeader>
              <CardTitle>{t('banner')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="banner.redirectType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('redirectType')}</FormLabel>
                    <ShadcnSelect value={field.value ?? CollectionRedirectType.LIST_OF_PRESETS} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('selectRedirectType')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {/* Outros tipos comentados - ainda não estão prontos */}
                        {/* <SelectItem value={CollectionRedirectType.PACKS}>{t('packs')}</SelectItem> */}
                        {/* <SelectItem value={CollectionRedirectType.CREATORS}>{t('creators')}</SelectItem> */}
                        {/* <SelectItem value={CollectionRedirectType.LIST_OF_PACKS}>{t('listOfPacks')}</SelectItem> */}
                        <SelectItem value={CollectionRedirectType.LIST_OF_PRESETS}>{t('listOfPresets')}</SelectItem>
                        {/* <SelectItem value={CollectionRedirectType.SUBSCRIPTION}>{t('subscription')}</SelectItem> */}
                      </SelectContent>
                    </ShadcnSelect>
                    <FormDescription>{t('redirectTypeDescription')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="banner.isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">{t('active')}</FormLabel>
                      <FormDescription>{t('bannerActiveDescription')}</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="banner.startDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('startDate')}</FormLabel>
                      <DatePicker
                        date={field.value}
                        onSelect={field.onChange}
                        placeholder={t('selectStartDate')}
                      />
                      <FormDescription>{t('startDateDescription')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="banner.endDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('endDate')}</FormLabel>
                      <DatePicker
                        date={field.value}
                        onSelect={field.onChange}
                        placeholder={t('selectEndDate')}
                      />
                      <FormDescription>{t('endDateDescription')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Banner Image */}
              <FileUpload
                label={t('bannerImage')}
                description={t('bannerImageDescription')}
                preview={bannerPreview}
                onFileChange={(file) => {
                  setBannerFile(file);
                  if (!file) {
                    setBannerPreview(null);
                  }
                }}
              />
            </CardContent>
          </Card>

          <FormFooter isLoading={isSubmitting}>
            <div className="flex gap-3 justify-between w-full">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/mobile/collections')}
                disabled={isSubmitting}
              >
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('saving') : isEdit ? t('saveChanges') : t('createCollection')}
              </Button>
            </div>
          </FormFooter>
        </form>
      </Form>
    </div>
  );
}
